import React, {useEffect, useState, useCallback} from "react";
import axios from "axios";
import {useAuth} from "../useAuth";
import {Brand} from "./objects/Brand";
import {LanguageTaggedString} from "./objects/LanguageTaggedString";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Button} from "primereact/button";
import {Panel} from "primereact/panel";
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup';
import {Meta} from "./objects/Meta";
import {fetchObjects, createObject, deleteFromObjectsGrid} from "../services/CRUD";

interface DataObject {
    deleted: boolean;
    id: string;
    meta: Meta;
    at_context?: string;
    at_type?: string;
    identifier?: string;
    name: LanguageTaggedString[];
    description?: LanguageTaggedString[];
    slogan?: LanguageTaggedString[];
    sameAs?: string[];
    url?: string;
    logo?: string;
    tags: { name: string; val: string }[];
}

const Brands = () => {

    const {ogToken, ogAccessPoint} = useAuth();

    const [brands, setBrands] = useState([]);
    const [newBrandDialogVisible, setNewBrandDialogVisible] = useState(false);

    /* Fields on new brand form */
    const [identifier, setIdentifier] = useState('');
    const [names, setNames] = useState<{ value: string; language: string }[]>([]);
    const [descriptions, setDescriptions] = useState<{ value: string; language: string }[]>([]);
    const [slogans, setSlogans] = useState<{ value: string; language: string }[]>([]);
    const [sameAs, setSameAs] = useState<string[]>([]);
    const [url, setUrl] = useState('');
    const [logo, setLogo] = useState('');

    const [tagNameValuePairs, setTagNameValuePairs] = useState<{ name: string, val: string }[]>([]);

    /* Fields used in the editing functionality */
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);

    const [showNameValidation, setShowNameValidation] = useState(false);

    /* A popup to show the user a command to get their brands from a terminal */
    const [showCurlDialog, setShowCurlDialog] = useState(false);

    const objectsGridBrandsUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/brands/';

    const fetchBrands = useCallback(async () => {
        console.log(ogAccessPoint);
        fetchObjects(objectsGridBrandsUrl + "?from=0&size=1000",
            ogAccessPoint,
            ogToken)
            .then(responseArray => {
                setBrands(responseArray || []);
            }).catch(error => {
                console.error('Fetching brands failed:', error);
                setBrands([]);
        });
    }, [ogAccessPoint, ogToken, objectsGridBrandsUrl]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands])

    const createBrand = async () => {

        setShowNameValidation(false);
        if (!names.length || names.some(n => !n.value)) {
            setShowNameValidation(true);
            return;
        }

        const newBrand: Brand = {
            at_context: "https://schema.org",
            at_type: "Brand",
            identifier: identifier || undefined,
            name: names.map(n => ({ at_value: n.value, at_language: n.language })),
            description: descriptions.length > 0 ? descriptions.map(desc => ({
                at_value: desc.value,
                at_language: desc.language
            })) : undefined,
            slogan: slogans.length > 0 ? slogans.map(s => ({ at_value: s.value, at_language: s.language })) : undefined,
            sameAs: sameAs.length > 0 ? sameAs : undefined,
            url: url || undefined,
            logo: logo || undefined,
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            }))
        };

         createObject(objectsGridBrandsUrl,
                ogAccessPoint,
                ogToken,
                newBrand)
             .then(response => {
                setNewBrandDialogVisible(false);
                console.log("New Brand created. Refreshing...");
                fetchBrands();
         }).catch(error => console.error('Adding brand failed:', error));

    }

    // Helper function to render language-tagged strings
    const renderLanguageTaggedStrings = (strings: LanguageTaggedString[]) => {
        return strings.map((str, index) => (
            <div key={index}>{`${str.at_value} (${str.at_language})`}</div>
        ));
    };

    const renderName = (rowData: DataObject) => {
        return renderLanguageTaggedStrings(rowData.name);
    };

    const renderDescription = (rowData: DataObject) => {
        return rowData.description ? renderLanguageTaggedStrings(rowData.description) : null;
    };

    const renderSlogan = (rowData: DataObject) => {
        return rowData.slogan ? renderLanguageTaggedStrings(rowData.slogan) : null;
    };

    const renderSameAs = (rowData: DataObject) => {
        return rowData.sameAs ? rowData.sameAs.map((url, index) => (
            <div key={index}>{url}</div>
        )) : null;
    };

    const renderTags = (rowData: DataObject) => {
        return rowData.tags.map(attr => (
            <div key={attr.name}>{`${attr.name}: ${attr.val}`}</div>
        ));
    };

    // Add new sameAs URL
    const addSameAs = () => {
        setSameAs([...sameAs, '']);
    };

    // Update sameAs URL
    const updateSameAs = (index: number, value: string) => {
        const updatedSameAs = [...sameAs];
        updatedSameAs[index] = value;
        setSameAs(updatedSameAs);
    };

    // Remove sameAs URL
    const removeSameAs = (index: number) => {
        const updatedSameAs = sameAs.filter((_, i) => i !== index);
        setSameAs(updatedSameAs);
    };

    // Add new name-value pair
    const addTagNameValuePair = () => {
        setTagNameValuePairs([...tagNameValuePairs, {name: '', val: ''}]);
    };

    // Update name-value pair
    const updateTagNameValuePair = (index: number, field: 'name' | 'val', value: string) => {
        const updatedPairs = [...tagNameValuePairs];
        updatedPairs[index][field] = value;
        setTagNameValuePairs(updatedPairs);
    };

    // Remove name-value pair
    const removeTagNameValuePair = (index: number) => {
        const updatedPairs = tagNameValuePairs.filter((_, i) => i !== index);
        setTagNameValuePairs(updatedPairs);
    };

    const tagsPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Tags [Optional]</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-plus"
                        label="Add New Tag"
                        onClick={addTagNameValuePair}
                    />
                </div>
            </div>
        );
    };

    const sameAsPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Same As URLs [Optional]</span>
                    <Button
                        icon="pi pi-info-circle"
                        className="p-button-text p-button-sm"
                        tooltip="URL of a reference Web page that unambiguously indicates the item's identity. E.g. the URL of the item's Wikipedia page, Wikidata entry, or official website."
                    />
                </div>
                <div>
                    <Button
                        icon="pi pi-plus"
                        label="Add New URL"
                        onClick={addSameAs}
                    />
                </div>
            </div>
        );
    };

    const handleEdit = (row: DataObject) => {
        console.log('Edit', row);

        setIsEditMode(true);
        setSelectedObjectId(row.id);
        setIdentifier(row.identifier || '');
        
        // Set names (all)
        if (row.name && row.name.length > 0) {
            setNames(row.name.map(n => ({ value: n.at_value, language: n.at_language })));
        } else {
            setNames([]);
        }

        // Set descriptions (all)
        if (row.description && row.description.length > 0) {
            setDescriptions(row.description.map(d => ({ value: d.at_value, language: d.at_language })));
        } else {
            setDescriptions([]);
        }

        // Set slogans (all)
        if (row.slogan && row.slogan.length > 0) {
            setSlogans(row.slogan.map(s => ({ value: s.at_value, language: s.at_language })));
        } else {
            setSlogans([]);
        }

        setSameAs(row.sameAs || []);
        setUrl(row.url || '');
        setLogo(row.logo || '');
        setTagNameValuePairs(row.tags);
        setNewBrandDialogVisible(true);
    };

    const updateBrand = async () => {
        console.log("Update Brand...");
        const updatedBrand: Brand = {
            id: selectedObjectId!,
            at_context: "https://schema.org",
            at_type: "Brand",
            identifier: identifier || undefined,
            name: names.map(n => ({ at_value: n.value, at_language: n.language })),
            description: descriptions.length > 0 ? descriptions.map(desc => ({
                at_value: desc.value,
                at_language: desc.language
            })) : undefined,
            slogan: slogans.length > 0 ? slogans.map(s => ({ at_value: s.value, at_language: s.language })) : undefined,
            sameAs: sameAs.length > 0 ? sameAs : undefined,
            url: url || undefined,
            logo: logo || undefined,
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            })),
        };

        const updateUrl = objectsGridBrandsUrl + selectedObjectId;
        console.log(updateUrl);
        console.log('Updating brand:', updatedBrand);

        return axios.put(updateUrl,
            updatedBrand, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            }).then(response => {
            console.log('Updated successfully:', response.data);
            setNewBrandDialogVisible(false);
            fetchBrands();
        }).catch(error => console.error('Updating brand failed:', error));
    };

    const newBrandDialogFooterContent = (
        <div>
            <Button label="Cancel"
                    icon="pi pi-times"
                    onClick={() => setNewBrandDialogVisible(false)}
                    className="p-button-text"/>
            <Button
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={isEditMode ? updateBrand : createBrand}
                autoFocus
            />
        </div>
    );

    const closeDialog = () => {
        setNewBrandDialogVisible(false);
        setIsEditMode(false);
        setSelectedObjectId(null);
        setIdentifier('');
        setNames([]);
        setDescriptions([]);
        setSlogans([]);
        setSameAs([]);
        setUrl('');
        setLogo('');
        setTagNameValuePairs([]);
    };

    const handleDelete = (row: DataObject, event: React.MouseEvent<HTMLButtonElement>) => {

        confirmPopup({
            target: event.currentTarget,
            message: 'Do you want to delete this Brand?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => {
                console.log('Delete', row);
                deleteBrandFromObjectsGrid(row).then((response) => {
                    console.log('Response from delete:', response);
                    fetchBrands();
                })
            },
            reject: () => {
                console.log('Cancel delete');
            }
        })
    };

    const deleteBrandFromObjectsGrid = async (row: DataObject) => {

        deleteFromObjectsGrid(objectsGridBrandsUrl + row.id,
            ogAccessPoint,
            ogToken)
            .then(response => {
                console.log('Deleted successfully:', response);

                // We need to wait a couple of seconds here. It takes OG a bit to delete the object.
                setTimeout(() => {
                    console.log('Refreshing Brands after delete...');
                    fetchBrands();
                }, 2000);

            })
            .catch(error => {
               console.error('DeleteFailed:', error);
            });
    }

    const renderActions = (rowData: DataObject) => {
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text aria-label="Edit" onClick={() => handleEdit(rowData)}/>
                <Button icon="pi pi-times" rounded text severity="danger" aria-label="Delete"
                        onClick={(e) => handleDelete(rowData, e)}/>
            </div>
        );
    };

    const brandsCurlDialogHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold text-lg">Brands</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-code"
                        label="Show cURL"
                        onClick={() => setShowCurlDialog(true)}
                    />
                </div>
            </div>
        );
    };

    const curlCommand = `curl -X GET '${objectsGridBrandsUrl}' \\
                  -H "Authorization: Bearer ${ogToken}" \\
                  -H "X-ObjectsGrid-AccessPoint: ${ogAccessPoint}"`;

    return (
        <div>
            <Panel headerTemplate={brandsCurlDialogHeader}>
                <Button
                    label="Create Brand"
                    icon="pi pi-plus"
                    className="mb-2 mr-2"
                    onClick={() => {
                        setIsEditMode(false);
                        setSelectedObjectId(null);
                        setIdentifier('');
                        setNames([]);
                        setDescriptions([]);
                        setSlogans([]);
                        setSameAs([]);
                        setUrl('');
                        setLogo('');
                        setTagNameValuePairs([]);
                        setNewBrandDialogVisible(true);
                    }}
                />
                <Button label="Refresh" icon="pi pi-refresh" className="mb-2" onClick={fetchBrands}/>
                <div className="datatable-container">
                    <DataTable value={brands}
                               paginator
                               stripedRows
                               rows={100}
                                emptyMessage="No Brands were found">
                        {/* Render columns */}
                        <Column field="id" header="ID"/>
                        <Column field="identifier" header="Identifier"/>
                        <Column header="Name" body={renderName}/>
                        <Column header="Description" body={renderDescription}/>
                        <Column header="Slogan" body={renderSlogan}/>
                        <Column header="Same As" body={renderSameAs}/>
                        <Column field="url" header="URL"/>
                        <Column field="logo" header="Logo"/>
                        <Column field="tags" header="Tags" body={renderTags}/>
                        <Column field="meta.createdAt" header="Created At"/>
                        <Column field="meta.createdBy" header="Created By"/>
                        <Column header="Actions" body={renderActions} style={{width: '120px'}}/>
                    </DataTable>
                </div>

                <Dialog header={isEditMode ? "Edit Brand" : "Create New Brand"}
                        visible={newBrandDialogVisible}
                        style={{width: '70vw'}}
                        onHide={closeDialog}
                        footer={newBrandDialogFooterContent}>
                    <div className="p-fluid">
                        <div className="p-field mb-2">
                            <label htmlFor="identifier">Identifier</label>
                            <InputText
                                id="identifier"
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                tooltip="Unique identifier for the Brand. This will be generated by ObjectsGrid when the Brand is created."
                                readOnly
                                style={{ backgroundColor: '#f5f5f5', color: '#6c757d' }}
                            />
                        </div>

                        <div className="p-field mb-2">
                            <label htmlFor="url">Object URL</label>
                            <InputText
                                id="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                tooltip="URL that will give you this Brand object from cUrl/Javascript/Python/etc. This will be generated by ObjectsGrid when the Brand is created."
                                readOnly
                                style={{ backgroundColor: '#f5f5f5', color: '#6c757d' }}
                            />
                        </div>

                        <div className="p-field mb-2">
                            <label className="flex align-items-center gap-2">
                                Name(s) [Required]
                                <Button
                                    icon="pi pi-info-circle"
                                    className="p-button-text p-button-sm"
                                    tooltip="Multiple names - allows for translations where applicable."
                                />
                            </label>
                            {names.map((n, idx) => (
                                <div key={idx} className="flex align-items-center mb-2 gap-2">
                                    <InputText
                                        value={n.value}
                                        onChange={e => {
                                            const newNames = [...names];
                                            newNames[idx].value = e.target.value;
                                            setNames(newNames);
                                        }}
                                        required
                                        style={{ width: '60%' }}
                                    />
                                    <InputText
                                        value={n.language}
                                        onChange={e => {
                                            const newNames = [...names];
                                            newNames[idx].language = e.target.value;
                                            setNames(newNames);
                                        }}
                                        placeholder="en"
                                        style={{ width: '100px' }}
                                    />
                                    <Button icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => setNames(names.filter((_, i) => i !== idx))} />
                                </div>
                            ))}
                            <Button icon="pi pi-plus" label="Add Name" onClick={() => setNames([...names, { value: '', language: 'en' }])} className="mt-2" />
                            {showNameValidation && <small className="p-error">At least one name is required and all names must have a value.</small>}
                        </div>

                        <div className="p-field mb-2">
                            <label className="flex align-items-center gap-2">
                                Description(s)
                                <Button
                                    icon="pi pi-info-circle"
                                    className="p-button-text p-button-sm"
                                    tooltip="Multiple descriptions - allows for translations where applicable."
                                />
                            </label>
                            {descriptions.map((desc, idx) => (
                                <div key={idx} className="flex align-items-center mb-2 gap-2">
                                    <InputTextarea
                                        value={desc.value}
                                        onChange={e => {
                                            const newDescs = [...descriptions];
                                            newDescs[idx].value = e.target.value;
                                            setDescriptions(newDescs);
                                        }}
                                        rows={2}
                                        autoResize
                                        style={{ width: '60%' }}
                                    />
                                    <InputText
                                        value={desc.language}
                                        onChange={e => {
                                            const newDescs = [...descriptions];
                                            newDescs[idx].language = e.target.value;
                                            setDescriptions(newDescs);
                                        }}
                                        placeholder="en"
                                        style={{ width: '100px' }}
                                    />
                                    <Button icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => setDescriptions(descriptions.filter((_, i) => i !== idx))} />
                                </div>
                            ))}
                            <Button icon="pi pi-plus" label="Add Description" onClick={() => setDescriptions([...descriptions, { value: '', language: 'en' }])} className="mt-2" />
                        </div>

                        <div className="p-field mb-2">
                            <label className="flex align-items-center gap-2">
                                Slogan(s)
                                <Button
                                    icon="pi pi-info-circle"
                                    className="p-button-text p-button-sm"
                                    tooltip="Multiple descriptions - allows for translations where applicable."
                                />
                            </label>
                            {slogans.map((s, idx) => (
                                <div key={idx} className="flex align-items-center mb-2 gap-2">
                                    <InputText
                                        value={s.value}
                                        onChange={e => {
                                            const newSlogans = [...slogans];
                                            newSlogans[idx].value = e.target.value;
                                            setSlogans(newSlogans);
                                        }}
                                        style={{ width: '60%' }}
                                    />
                                    <InputText
                                        value={s.language}
                                        onChange={e => {
                                            const newSlogans = [...slogans];
                                            newSlogans[idx].language = e.target.value;
                                            setSlogans(newSlogans);
                                        }}
                                        placeholder="en"
                                        style={{ width: '100px' }}
                                    />
                                    <Button icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => setSlogans(slogans.filter((_, i) => i !== idx))} />
                                </div>
                            ))}
                            <Button icon="pi pi-plus" label="Add Slogan" onClick={() => setSlogans([...slogans, { value: '', language: 'en' }])} className="mt-2" />
                        </div>

                        <div className="p-field mb-2">
                            <label htmlFor="logo">Logo URL</label>
                            <InputText
                                id="logo"
                                value={logo}
                                onChange={(e) => setLogo(e.target.value)}
                            />
                        </div>

                        <Panel headerTemplate={sameAsPanelHeader} className="mb-2">
                            {sameAs.map((url, index) => (
                                <div key={index} className="p-d-flex p-ai-center p-jc-between p-mb-2 mb-2">
                                    <InputText
                                        placeholder="URL"
                                        value={url}
                                        onChange={(e) => updateSameAs(index, e.target.value)}
                                        style={{marginRight: '1rem', flex: 1}}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-danger p-button-outlined"
                                        onClick={() => removeSameAs(index)}
                                    />
                                </div>
                            ))}
                        </Panel>

                        <Panel headerTemplate={tagsPanelHeader} className="mb-2">
                            {tagNameValuePairs.map((pair, index) => (
                                <div key={index} className="p-d-flex p-ai-center p-jc-between p-mb-2 mb-2">
                                    <InputText
                                        placeholder="Name"
                                        value={pair.name}
                                        onChange={(e) => updateTagNameValuePair(index, 'name', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <InputText
                                        placeholder="Value"
                                        value={pair.val}
                                        onChange={(e) => updateTagNameValuePair(index, 'val', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-danger p-button-outlined"
                                        onClick={() => removeTagNameValuePair(index)}
                                    />
                                </div>
                            ))}
                        </Panel>

                    </div>
                </Dialog>

                <ConfirmPopup/>

            </Panel>
            <Dialog
                header="cURL Command"
                visible={showCurlDialog}
                style={{width: '60vw'}}
                onHide={() => setShowCurlDialog(false)}
            >
                <div>
                    <p>You can use this command to get your Brands from a terminal...</p>
                    <pre className="bg-gray-100 p-3 rounded overflow-auto">
                                <code>{curlCommand}</code>
                            </pre>
                    <Button
                        label="Copy to Clipboard"
                        icon="pi pi-copy"
                        onClick={() => navigator.clipboard.writeText(curlCommand)}
                        className="mt-2"
                    />
                </div>
            </Dialog>
        </div>);

};

export default Brands; 